import { faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import { memo, useContext, useMemo } from 'react';

import PageHeader from '@/components/PageHeader';
import ENVIRONMENTS from '@/constants/environments';
import HOSTS from '@/constants/hosts';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import PersonContext from '@/pages/Person/PersonContext';
import CommunityRepository from '@/repositories/CommunityRepository';
import { PersonGroupMembership } from '@/types/community';
import Container from '@/ui/components/Container';
import DataList from '@/ui/components/DataList';
import DataRow from '@/ui/components/DataList/DataRow';
import DualTime from '@/ui/components/DualTime/DualTime';
import ListCard from '@/ui/components/ListCard';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';

const PersonGroupMemberships = (): JSX.Element | null => {
  const { person } = useContext(PersonContext);
  const { getPersonGroupMemberships } = CommunityRepository();
  const { host } = useContext(AppContext);
  const projectId = useMemo(() => {
    if (host === HOSTS[ENVIRONMENTS.LOCAL] || host === HOSTS[ENVIRONMENTS.STAGING]) {
      return 1243037;
    } else {
      return 1251315;
    }
  }, [host]);
  const getStreamLink = (groupId: string) => {
    const baseStreamURL = 'https://dashboard.getstream.io/app';
    const fillerStreamURL = 'chat/explorer?path=channels%2Cchannel~messaging%3A';

    return `${baseStreamURL}/${projectId}/${fillerStreamURL}${groupId}%2Cmembers`;
  };
  const { data, isPending } = useAPI({
    queryKey: ['getPersonGroupMemberships', person.id],
    queryFn: async () => {
      return await getPersonGroupMemberships({ personId: person.id });
    }
  });

  const group_memberships = data?.data;

  return isPending ? (
    <LoadingSkeleton spinnerProps={{ label: 'Loading Community Information' }} />
  ) : (
    <Container type="page">
      <PageHeader headingText="Circles" />

      {group_memberships?.length ? (
        group_memberships.map((membership: PersonGroupMembership) => (
          <ListCard
            key={membership.id}
            headingActions={
              <div className="flex justify-between items-center">
                <a
                  href={getStreamLink(membership.group.id)}
                  target="_blank"
                  className="hover:text-gray-900 p-1.5"
                  rel="noreferrer">
                  <div className="text-gray-600 hover:text-gray-800">
                    <FontAwesomeIcon icon={faLink} />
                  </div>
                </a>
              </div>
            }
            title={membership.group.title}>
            <DataList>
              <DataRow label="Status" content={membership.status} />
              <DataRow
                label={'Last Change'}
                content={
                  <DualTime
                    memberTZ={person.time_zone}
                    dateTime={new Date(membership.updated_at)}
                  />
                }
              />
            </DataList>
          </ListCard>
        ))
      ) : (
        <p className="max-w-md mx-auto mb-3">
          This person doesn&apos;t have access to any Circles.{' '}
          <a
            className="text-blue-700 underline"
            href="https://docs.google.com/presentation/d/1Q5aBopJ2wFSveBFiHX6aw3hvreWA78bEEY0hCDpc54k/edit#slide=id.g243fbd9ed3a_0_3"
            target="_blank"
            rel="noreferrer">
            More info on Circle eligibility.
          </a>
        </p>
      )}
    </Container>
  );
};

export default memo(PersonGroupMemberships);
